import type { HistoricalEventType } from './types';

export const HISTORICAL_EVENT_TYPES: { type: HistoricalEventType; label: string }[] = [
    { type: 'political', label: 'Political & Dynastic' },
    { type: 'building', label: 'Building & Construction' },
    { type: 'migration', label: 'Migration & Settlement' },
    { type: 'conflict', label: 'War & Conflict' },
    { type: 'biographical', label: 'Biographical' },
    { type: 'cultural', label: 'Cultural & Social' },
    { type: 'natural', label: 'Natural Events' },
    { type: 'theological', label: 'Theological & Religious' },
    { type: 'military', label: 'Military & Conquest' },
    { type: 'religious', label: 'Religious & Temple' },
    { type: 'economic', label: 'Trade & Economy' },
    { type: 'technological', label: 'Technological & Infrastructure' },
    { type: 'diaspora', label: 'Exile & Diaspora' },
    { type: 'legal', label: 'Legal & Judicial' },
    { type: 'israel-ruler', label: 'Israel/Judah Rulers' },
    { type: 'israel-prophet', label: 'Israel Prophets' },
    { type: 'biblical-narrative', label: 'Biblical Narrative' },
    { type: 'biblical-composition', label: 'Biblical Composition' },
];

export const COST_RATES = {
    place: 0.0005,
    event: 0.0004,
};

export const getPromptAndImageStats = (
    allPlaces: string[],
    existingGeoRefNames: Set<string>,
    imagePromptDetails: Map<string, any>,
    getPlaceByName: (name: string) => any
) => {
    let placesWithPrompts = 0;
    let promptsV2 = 0;
    let promptsV1 = 0;
    let placesWithIncompletePrompts = 0;
    let placesWithRuinsImages = 0;
    let totalReconstructionImages = 0;
    let placesWithIncompleteImages = 0;
    let totalMissingRuins = 0;
    let totalMissingReconstructions = 0;
    let totalPlacesWithoutPrompts = 0;

    for (const placeName of allPlaces) {
        const promptData = imagePromptDetails.get(placeName);
        const placeData = getPlaceByName(placeName);

        if (promptData) {
            placesWithPrompts++;
            const v = promptData.generation?.version || 1;
            if (v >= 2) promptsV2++;
            else promptsV1++;

            if (!promptData.ruinsPrompt) placesWithIncompletePrompts++;
        } else {
            // Place has no prompts at all
            totalPlacesWithoutPrompts++;
        }

        if (placeData?.images?.ruinsImageUrl) {
            placesWithRuinsImages++;
        }

        if (placeData?.images?.reconstructionsByPeriod) {
            const reconstructions = Object.values(placeData.images.reconstructionsByPeriod).length;
            totalReconstructionImages += reconstructions;
        }

        const missingInfo = getMissingImages(placeName, imagePromptDetails, getPlaceByName);
        if (missingInfo.missing.length > 0) {
            placesWithIncompleteImages++;

            const ruinsMissing = missingInfo.missing.includes('ruins');
            if (ruinsMissing) totalMissingRuins++;
            totalMissingReconstructions += missingInfo.missing.length - (ruinsMissing ? 1 : 0);

            // Debug logging for first few places
            if (placesWithIncompleteImages <= 3) {
                console.log(`[Admin] ${placeName} missing:`, missingInfo.missing,
                    `ruins: ${ruinsMissing ? 1 : 0}, eras: ${missingInfo.missing.length - (ruinsMissing ? 1 : 0)}`);
            }
        }
    }

    return {
        placesWithPrompts,
        promptsV2,
        promptsV1,
        placesWithIncompletePrompts,
        placesWithRuinsImages,
        totalReconstructionImages,
        placesWithIncompleteImages,
        totalMissingImages: totalMissingRuins + totalMissingReconstructions,
        totalMissingRuins,
        totalMissingReconstructions,
        totalPlacesWithoutPrompts
    };
};

export const getMissingPeriods = (
    placeName: string,
    imagePromptDetails: Map<string, any>,
    getPlaceByName: (name: string) => any
) => {
    const promptData = imagePromptDetails.get(placeName);
    const placeData = getPlaceByName(placeName);

    const promptPeriods = promptData?.reconstructionPromptsByPeriod ? Object.keys(promptData.reconstructionPromptsByPeriod) : [];
    const imagePeriods = placeData?.images?.reconstructionsByPeriod ? Object.keys(placeData.images.reconstructionsByPeriod) : [];

    const missing = promptPeriods.filter(p => !imagePeriods.includes(p));

    return {
        total: promptPeriods.length,
        covered: imagePeriods.length,
        missing
    };
};

export const getMissingImages = (
    placeName: string,
    imagePromptDetails: Map<string, any>,
    getPlaceByName: (name: string) => any
) => {
    const promptData = imagePromptDetails.get(placeName);
    const placeData = getPlaceByName(placeName);

    const promptCount = (promptData?.ruinsPrompt ? 1 : 0) + (promptData?.reconstructionPromptsByPeriod ? Object.keys(promptData.reconstructionPromptsByPeriod).length : 0);
    const imageCount = (placeData?.images?.ruinsImageUrl ? 1 : 0) + (placeData?.images?.reconstructionsByPeriod ? Object.keys(placeData.images.reconstructionsByPeriod).length : 0);

    const missing: string[] = [];
    if (promptData?.ruinsPrompt && !placeData?.images?.ruinsImageUrl) missing.push('ruins');
    if (promptData?.reconstructionPromptsByPeriod) {
        Object.keys(promptData.reconstructionPromptsByPeriod).forEach(era => {
            if (!placeData?.images?.reconstructionsByPeriod?.[era]) {
                missing.push(era);
            }
        });
    }

    return {
        total: promptCount,
        generated: imageCount,
        missing
    };
};
